import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Flame, Footprints, Activity, Target, CheckCircle2, XCircle,
  Sparkles, ChevronRight, CalendarDays, Bell,
} from 'lucide-react';
import {
  fetchRecommendations, fetchMyActivities, fetchWeeklySteps, saveSteps,
} from '../../api/move';
import type {
  MoveEvent, MoveRouteSummary, MoveActivity, WeeklySteps, Recommendations,
} from '../../api/move';
import { SecureStorage } from '../../storage/SecureStorage';
import { API_BASE } from '../../config';
import api from '../../api/Client';
import { scheduleManyMoveEventReminders } from '../../notifications/moveEvents';
import segoviaSiInnovaLogo from '../../assets/segoviasiinnova.jpeg';
import styles from '../../styles/Move.module.css';

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function formatDistance(m: number) {
  if (m >= 1000) return `${(m / 1000).toFixed(1)} km`;
  return `${m} m`;
}

function imageUrl(filename?: string | null) {
  if (!filename) return null;
  if (filename.startsWith('http')) return filename;
  return `${API_BASE}/api/move_images/${filename}`;
}

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

export default function MoveHome() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<MoveEvent[]>([]);
  const [joinedEvents, setJoinedEvents] = useState<MoveEvent[]>([]);
  const [routes, setRoutes] = useState<MoveRouteSummary[]>([]);
  const [recommendation, setRecommendation] = useState<Recommendations['recommendation'] | null>(null);
  const [activities, setActivities] = useState<MoveActivity[]>([]);
  const [weekly, setWeekly] = useState<WeeklySteps | null>(null);
  const [nudge, setNudge] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [savingStepGoal, setSavingStepGoal] = useState(false);
  const [stepGoalError, setStepGoalError] = useState<string | null>(null);
  const [recoOpen, setRecoOpen] = useState(false);

  async function loadAll() {
    try {
      const [recs, list, w] = await Promise.all([
        fetchRecommendations().catch(() => ({ events: [], joined_events: [], routes: [], nudge: null, recommendation: null })),
        fetchMyActivities().catch(() => []),
        fetchWeeklySteps().catch(() => null as WeeklySteps | null),
      ]);
      setEvents(recs.events);
      setJoinedEvents(recs.joined_events ?? []);
      setRoutes(recs.routes);
      setNudge(recs.nudge);
      setRecommendation(recs.recommendation ?? null);
      setActivities(list);
      setWeekly(w);
      scheduleManyMoveEventReminders(recs.joined_events ?? []).catch(() => undefined);
    } catch (e) {
      console.warn('MoveHome load error', e);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const stored = await SecureStorage.getUser?.();
        if (stored?.fullName) setUserName(String(stored.fullName).split(' ')[0] || '');
        if (stored?.id) {
          try {
            const r = await api.get<{ data: { profile_photo?: string | null } }>(`/api/customers/get/${stored.id}`);
            const photo = r.data?.data?.profile_photo;
            if (photo) setAvatarUrl(`${API_BASE}/api/profile_pic/${photo}`);
          } catch { /* no critical */ }
        }
        await loadAll();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function handleSetStepGoal(completed: boolean, goalTarget?: number) {
    setStepGoalError(null);
    setSavingStepGoal(true);
    try {
      await saveSteps({
        fecha: todayKey(),
        completed,
        goal_target: goalTarget || weekly?.goalTarget || 10000,
      });
      await loadAll();
    } catch (e: any) {
      setStepGoalError(e?.response?.data?.error || e?.message || 'No se pudo guardar el objetivo de pasos');
    } finally {
      setSavingStepGoal(false);
    }
  }

  if (loading) {
    return (
      <div className={`move-app ${styles.page}`}>
        <p className={styles.empty}>Cargando…</p>
      </div>
    );
  }

  // ----- métricas reales -----
  const days = weekly?.days ?? [];
  const todayStepGoalCompleted = Boolean(weekly?.todayGoalCompleted);
  const completedStepDays = weekly?.completedDays ?? days.filter(d => d.completed).length;
  const stepGoalTarget = weekly?.goalTarget ?? 10000;

  const weekSessions = activities.filter(a => {
    const d = new Date(a.fecha);
    const diff = (Date.now() - d.getTime()) / 86400000;
    return diff <= 7;
  }).length;

  // streak: días consecutivos con actividad o pasos
  const dayKeys = new Set<string>();
  activities.forEach(a => dayKeys.add(new Date(a.fecha).toISOString().slice(0, 10)));
  days.filter(d => d.completed).forEach(d => dayKeys.add(d.fecha));
  let streak = 0;
  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);
  while (dayKeys.has(cursor.toISOString().slice(0, 10))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }

  const upcomingEvent = events[0];
  const nearbyJoinedEvent = joinedEvents[0];
  const mainRoute = routes[0];

  return (
    <div className={`move-app ${styles.page}`}>
      <Hero
        name={userName}
        avatarUrl={avatarUrl}
        streakDays={streak}
        stepGoalCompleted={todayStepGoalCompleted}
        stepGoalTarget={stepGoalTarget}
        savingStepGoal={savingStepGoal}
        onSetStepGoal={handleSetStepGoal}
      />

      {/* Quick stats */}
      <section className={styles.quickStats}>
        <div className={styles.statCard}>
          <Activity size={18} color="hsl(0 72% 44%)" style={{ margin: '0 auto' }} />
          <p className={styles.statValue}>{weekSessions}</p>
          <p className={styles.statLabel}>Sesiones</p>
        </div>
        <div className={styles.statCard}>
          {todayStepGoalCompleted
            ? <CheckCircle2 size={18} color="hsl(158 72% 38%)" style={{ margin: '0 auto' }} />
            : <XCircle size={18} color="hsl(18 94% 54%)" style={{ margin: '0 auto' }} />}
          <p className={styles.statValue}>{todayStepGoalCompleted ? 'Sí' : 'No'}</p>
          <p className={styles.statLabel}>Pasos hoy</p>
        </div>
        <div className={styles.statCard}>
          <Target size={18} color="hsl(18 94% 54%)" style={{ margin: '0 auto' }} />
          <p className={styles.statValue}>{completedStepDays}/7</p>
          <p className={styles.statLabel}>Días objetivo</p>
        </div>
      </section>

      {nearbyJoinedEvent && (
        <section className={styles.section} style={{ paddingTop: '1.15rem' }}>
          <div
            className={styles.card}
            style={{
              borderColor: 'hsl(0 72% 44% / 0.22)',
              background: 'linear-gradient(135deg, hsl(0 72% 44% / 0.08), var(--move-card) 62%)',
              cursor: 'pointer',
            }}
            onClick={() => navigate(`/move/events/${nearbyJoinedEvent.event_id}`)}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 14,
                  background: 'var(--move-grad-primary)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: 'var(--move-shadow-glow)',
                }}
              >
                <Bell size={18} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 800, color: 'hsl(0 72% 44%)' }}>
                  Evento cercano
                </p>
                <p className={styles.cardTitle} style={{ marginTop: 2 }}>
                  {nearbyJoinedEvent.titulo}
                </p>
                <p className={styles.cardSubtitle}>
                  {formatDate(nearbyJoinedEvent.fecha_inicio)}
                  {nearbyJoinedEvent.ubicacion_nombre ? ` · ${nearbyJoinedEvent.ubicacion_nombre}` : ''}
                </p>
              </div>
              <CalendarDays size={18} color="var(--move-muted-fg)" />
            </div>
          </div>
        </section>
      )}

      {stepGoalError && (
        <section
          className={styles.section}
          style={{
            paddingTop: nudge ? '1.1rem' : '1.5rem',
            paddingBottom: '1.15rem',
          }}
        >
          <div className={styles.card} style={{ borderColor: 'hsl(348 90% 64% / 0.3)' }}>
            <p style={{ margin: 0, color: 'hsl(348 90% 45%)', fontSize: 13 }}>
              {stepGoalError}
            </p>
          </div>
        </section>
      )}

      {/* Ruta recomendada */}
      {mainRoute && (
        <section className={styles.section} style={{ paddingTop: nudge ? 0 : '1.25rem' }}>
          <button className={styles.recoBubble} type="button" onClick={() => setRecoOpen(open => !open)}>
            <span className={styles.recoBubbleIcon}><Sparkles size={16} /></span>
            <span className={styles.recoBubbleText}>
              <strong>{recommendation?.title || 'Ruta recomendada por IA'}</strong>
              <small>{mainRoute.titulo} · {mainRoute.recommendation_score ?? 0}% match</small>
            </span>
            <ChevronRight size={17} className={recoOpen ? styles.recoBubbleChevronOpen : styles.recoBubbleChevron} />
          </button>

          {recoOpen && (
            <div className={styles.recoPopover}>
              <div className={styles.recoPopoverImg}>
                {imageUrl(mainRoute.imagen_principal)
                  ? <img src={imageUrl(mainRoute.imagen_principal) || ''} alt={mainRoute.titulo} />
                  : <div className={styles.recoHeroFallback} />
                }
              </div>
              <div className={styles.recoPopoverBody}>
                <p className={styles.recoPopoverTitle}>{mainRoute.titulo}</p>
                <p className={styles.recoReason}>{mainRoute.recommendation_reason || recommendation?.subtitle}</p>
                <div className={styles.recoMeta}>
                  <span>{formatDistance(mainRoute.distancia_m)}</span>
                  <span>+{mainRoute.desnivel_m} m</span>
                  <span>{mainRoute.duracion_min} min</span>
                </div>
                {mainRoute.recommendation_reasons?.length ? (
                  <div className={styles.recoReasonRow}>
                    {mainRoute.recommendation_reasons.map(reason => <span key={reason}>{reason}</span>)}
                  </div>
                ) : null}
                <button className={styles.recoAction} type="button" onClick={() => navigate(`/move/routes/${mainRoute.route_id}`)}>
                  Ver ruta
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Próximo evento */}
      {upcomingEvent && (
        <section className={styles.section}>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>Próximo evento</h2>
            <span className={styles.sectionLink} onClick={() => navigate('/move/events')}>
              Ver todos <ChevronRight size={12} />
            </span>
          </div>
          <div
            className={styles.cover}
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/move/events/${upcomingEvent.event_id}`)}
          >
            {upcomingEvent.imagen
              ? <img src={`${API_BASE}/api/move_images/${upcomingEvent.imagen}`} alt="" />
              : <div style={{ position: 'absolute', inset: 0, background: 'var(--move-grad-cool)' }} />
            }
            <div className={styles.coverOverlay} />
            <div className={styles.coverContent}>
              <div className={styles.coverBadge}>
                <Sparkles size={12} /> Recomendado para ti
              </div>
              <p className={styles.coverTitle}>{upcomingEvent.titulo}</p>
              <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, margin: '4px 0 0' }}>
                {formatDate(upcomingEvent.fecha_inicio)}{upcomingEvent.ubicacion_nombre ? ` · ${upcomingEvent.ubicacion_nombre}` : ''}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Rutas para ti */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Rutas para ti</h2>
          <span className={styles.sectionLink} onClick={() => navigate('/move/routes')}>
            Ver todas <ChevronRight size={12} />
          </span>
        </div>
        <div className={styles.cardList}>
          {routes.length <= 1 && <div className={styles.empty}>Aún no hay alternativas suficientes</div>}
          {routes.slice(1, 3).map(r => (
            <div key={r.route_id} className={`${styles.card} ${styles.cardClickable}`} onClick={() => navigate(`/move/routes/${r.route_id}`)}>
              <p className={styles.cardTitle}>{r.titulo}</p>
              <div className={styles.cardMeta}>
                <span className={`${styles.tag} ${styles.tagPrimary}`}>{r.tipo}</span>
                <span>{formatDistance(r.distancia_m)}</span>
                <span>+{r.desnivel_m} m</span>
                <span>★ {Number(r.rating_avg).toFixed(1)} ({r.votos_count})</span>
              </div>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}

// ===== Hero =====
interface HeroProps {
  name: string;
  avatarUrl: string | null;
  streakDays: number;
  stepGoalCompleted: boolean;
  stepGoalTarget: number;
  savingStepGoal: boolean;
  onSetStepGoal: (completed: boolean, goalTarget?: number) => void;
}

function Hero({ name, avatarUrl, streakDays, stepGoalCompleted, stepGoalTarget, savingStepGoal, onSetStepGoal }: HeroProps) {
  const navigate = useNavigate();
  const [goalInput, setGoalInput] = useState(String(stepGoalTarget || 10000));
  const parsedGoal = parseInt(goalInput, 10);
  const goalIsValid = Number.isFinite(parsedGoal) && parsedGoal >= 1000 && parsedGoal <= 100000;

  useEffect(() => {
    setGoalInput(String(stepGoalTarget || 10000));
  }, [stepGoalTarget]);

  function submitStepGoal(completed = stepGoalCompleted) {
    if (!goalIsValid) return;
    onSetStepGoal(completed, parsedGoal);
  }

  return (
    <header className={styles.hero}>
      <div className={styles.heroBg} />
      <div className={styles.heroOrb} />
      <div className={styles.heroInner}>
        <div className={styles.greetingRow}>
          <div>
            <p className={styles.greetingLabel}>Buenos días</p>
            <h1 className={styles.greetingName}>{name || 'Hola'}</h1>
          </div>
          <div className={styles.avatar} onClick={() => navigate('/move/profile')}>
            {avatarUrl
              ? <img src={avatarUrl} alt="perfil" />
              : <div style={{ width: '100%', height: '100%', background: 'var(--move-muted)' }} />
            }
            <div className={styles.avatarBadge}><Flame size={10} /></div>
          </div>
        </div>

        <div className={styles.streakCard}>
          <div className={styles.streakHead}>
            <div className={styles.streakLeft}>
              <div className={`${styles.iconBox} ${styles.energy}`}><Flame size={18} /></div>
              <div>
                <p className={styles.miniLabel}>Racha activa</p>
                <p className={styles.miniValue}>{streakDays} día{streakDays === 1 ? '' : 's'}</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/move/ranking')}
              style={{
                background: 'rgba(255,255,255,0.72)',
                color: 'var(--move-fg)', border: 0,
                padding: '6px 12px', borderRadius: 999,
                fontSize: 11, fontWeight: 700, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 4,
                boxShadow: 'var(--move-shadow-sm)',
              }}
            >
              <Target size={12} /> Ranking
            </button>
          </div>

          <div className={styles.stepsRow}>
            <div>
              <span className={styles.stepsBig}>{stepGoalCompleted ? 'Sí' : 'No'}</span>
              <span className={styles.stepsGoal}>hoy</span>
              <p className={styles.stepsCaption}>¿Has cumplido tu objetivo de pasos?</p>
            </div>
            {stepGoalCompleted
              ? <CheckCircle2 size={28} color="hsl(158 72% 38%)" />
              : <Footprints size={28} color="hsl(0 72% 44%)" />}
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            gap: 8,
            alignItems: 'end',
            marginBottom: 8,
          }}>
            <label style={{ display: 'grid', gap: 5, minWidth: 0 }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--move-muted-fg)' }}>
                Objetivo diario
              </span>
              <input
                type="number"
                min={1000}
                max={100000}
                step={500}
                inputMode="numeric"
                value={goalInput}
                onChange={e => setGoalInput(e.target.value)}
                style={{
                  minHeight: 38,
                  width: '100%',
                  border: `1px solid ${goalIsValid ? 'var(--move-border)' : 'hsl(348 90% 64%)'}`,
                  borderRadius: 12,
                  background: 'rgba(255,255,255,0.78)',
                  color: 'var(--move-fg)',
                  fontWeight: 800,
                  padding: '0 12px',
                  outline: 'none',
                }}
              />
            </label>
            <button
              type="button"
              disabled={savingStepGoal || !goalIsValid}
              onClick={() => submitStepGoal()}
              aria-label="Guardar objetivo de pasos"
              style={{
                minHeight: 38,
                border: '1px solid var(--move-border)',
                borderRadius: 12,
                background: 'rgba(255,255,255,0.72)',
                color: 'var(--move-fg)',
                fontWeight: 800,
                cursor: goalIsValid ? 'pointer' : 'not-allowed',
                opacity: savingStepGoal || !goalIsValid ? 0.62 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '0 12px',
                whiteSpace: 'nowrap',
              }}
            >
              <Target size={14} /> Guardar
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <button
              type="button"
              disabled={savingStepGoal}
              onClick={() => submitStepGoal(true)}
              style={{
                minHeight: 38,
                border: stepGoalCompleted ? '0' : '1px solid var(--move-border)',
                borderRadius: 12,
                background: stepGoalCompleted ? 'var(--move-grad-primary)' : 'rgba(255,255,255,0.72)',
                color: stepGoalCompleted ? '#fff' : 'var(--move-fg)',
                fontWeight: 800,
                cursor: 'pointer',
                opacity: savingStepGoal ? 0.7 : 1,
              }}
            >
              Sí
            </button>
            <button
              type="button"
              disabled={savingStepGoal}
              onClick={() => submitStepGoal(false)}
              style={{
                minHeight: 38,
                border: !stepGoalCompleted ? '0' : '1px solid var(--move-border)',
                borderRadius: 12,
                background: !stepGoalCompleted ? 'var(--move-muted)' : 'rgba(255,255,255,0.72)',
                color: 'var(--move-fg)',
                fontWeight: 800,
                cursor: 'pointer',
                opacity: savingStepGoal ? 0.7 : 1,
              }}
            >
              No
            </button>
          </div>
        </div>
        <div
          style={{
            marginTop: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '9px 11px',
            borderRadius: 16,
            background: 'rgba(255,255,255,0.16)',
            border: '1px solid rgba(255,255,255,0.22)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <span style={{ color: 'rgba(255,255,255,0.82)', fontSize: 11, fontWeight: 800 }}>
            Con el impulso de
          </span>
          <img
            src={segoviaSiInnovaLogo}
            alt="Segovia Si Innova"
            style={{ width: 138, maxWidth: '48%', height: 'auto', display: 'block' }}
          />
        </div>
      </div>
    </header>
  );
}
